import React, { useState } from 'react';
import { useAlchemy } from '../hooks/useAlchemy';
import { useDraggablePanel } from '../utils/panelDragUtils';
import '../styles/Alchemy.css';

interface AlchemyPanelProps {
  socketRef: React.MutableRefObject<any>;
  isOpen: boolean;
  onClose: () => void;
}

export const AlchemyPanel: React.FC<AlchemyPanelProps> = ({ socketRef, isOpen, onClose }) => {
  const { alchemyState, recipes, startBrewing, stopBrewing, isLoading, error } =
    useAlchemy(socketRef);
  const { handleHeaderMouseDown, style: dragStyle, panelRef } = useDraggablePanel(200, 250);

  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  if (!isOpen) return null;

  const selectedRecipe = recipes.find(r => r.id === selectedRecipeId);
  const xpPercent = alchemyState.nextLevelXp > 0
    ? ((alchemyState.xp / alchemyState.nextLevelXp) * 100)
    : 0;

  return (
    <div className="alchemy-panel" ref={panelRef} style={dragStyle}>
      <div className="alchemy-panel__header" onMouseDown={handleHeaderMouseDown}>
        <h2>Alchemy</h2>
        <button className="alchemy-panel__close" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Skill Stats */}
      <div className="alchemy-panel__stats">
        <div className="alchemy-stats">
          <div className="alchemy-stat">
            <span className="alchemy-stat__label">Level</span>
            <span className="alchemy-stat__value">{alchemyState.level}</span>
          </div>
          <div className="alchemy-stat">
            <span className="alchemy-stat__label">XP</span>
            <span className="alchemy-stat__value">{alchemyState.xp.toLocaleString()}</span>
          </div>
        </div>

        {/* XP Progress Bar */}
        <div className="alchemy-progress">
          <div className="alchemy-progress__bar">
            <div className="alchemy-progress__fill" style={{ width: `${xpPercent}%` }} />
          </div>
          <div className="alchemy-progress__text">
            {alchemyState.xp.toLocaleString()} / {alchemyState.nextLevelXp.toLocaleString()} XP
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="alchemy-error">{error}</div>}

      {/* Current Alchemy Status */}
      {alchemyState.isBrewing && alchemyState.currentRecipe && (
        <div className="alchemy-status">
          <div className="alchemy-status__recipe">{alchemyState.currentRecipe.name}</div>
          <div className="alchemy-status__progress">
            {alchemyState.itemsBrewed} / {alchemyState.batchSize} items
          </div>
          <button className="alchemy-stop-btn" onClick={stopBrewing}>
            Stop Brewing
          </button>
        </div>
      )}

      {/* Recipe Selection */}
      {!alchemyState.isBrewing && (
        <div className="alchemy-recipes">
          <h3>Available Recipes</h3>
          {isLoading ? (
            <div className="alchemy-loading">Loading recipes...</div>
          ) : recipes.length === 0 ? (
            <div className="alchemy-no-recipes">No recipes available at your level</div>
          ) : (
            <div className="alchemy-recipe-list">
              {recipes.map(recipe => (
                <div
                  key={recipe.id}
                  className={`alchemy-recipe ${selectedRecipeId === recipe.id ? 'active' : ''}`}
                  onClick={() => setSelectedRecipeId(recipe.id)}
                >
                  <div className="alchemy-recipe__name">{recipe.name}</div>
                  <div className="alchemy-recipe__meta">
                    <span className="alchemy-recipe__level">Lvl {recipe.requiredLevel}</span>
                    <span className="alchemy-recipe__xp">{recipe.baseXp} XP</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recipe Details & Start Alchemy */}
          {selectedRecipe && (
            <div className="alchemy-recipe-details">
              <h4>{selectedRecipe.name}</h4>
              <p className="alchemy-recipe-description">{selectedRecipe.description}</p>

              <div className="alchemy-recipe-info">
                <div className="alchemy-info-row">
                  <span>Required Level:</span>
                  <strong>{selectedRecipe.requiredLevel}</strong>
                </div>
                <div className="alchemy-info-row">
                  <span>Brew Time:</span>
                  <strong>{(selectedRecipe.baseBrewTime / 1000).toFixed(1)}s</strong>
                </div>
                <div className="alchemy-info-row">
                  <span>XP per Item:</span>
                  <strong>{selectedRecipe.baseXp}</strong>
                </div>
                {selectedRecipe.canBurn && (
                  <div className="alchemy-info-row">
                    <span>Max Burn Rate:</span>
                    <strong>{(selectedRecipe.baseBurnRate * 100).toFixed(0)}%</strong>
                  </div>
                )}
              </div>

              {/* Quantity Input */}
              <div className="alchemy-quantity">
                <label htmlFor="alchemy-quantity">Quantity:</label>
                <input
                  id="alchemy-quantity"
                  type="number"
                  min="1"
                  max="99"
                  value={quantity}
                  onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>

              {/* Start Alchemy Button */}
              <button
                className="alchemy-start-btn"
                onClick={() => {
                  startBrewing(selectedRecipe.id, quantity);
                  setSelectedRecipeId(null);
                }}
                disabled={alchemyState.level < selectedRecipe.requiredLevel}
              >
                Start Brewing ({quantity}x)
              </button>

              {alchemyState.level < selectedRecipe.requiredLevel && (
                <div className="alchemy-level-requirement">
                  Requires Level {selectedRecipe.requiredLevel}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
